import React, { useEffect, useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  X as CloseIcon,
  PackageX,
  TriangleAlert,
  Search as SearchIcon,
} from "lucide-react";
import { Axios } from "api/axios";
import { MecordColors } from "Constants/Color";
import MasterTableContainer from "../../TagsSearchPage/MasterFiltersTable/MasterTableContainer";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import {
  mergeDashboardFilters,
  DashboardColFilter,
  scopedDropdownProps,
} from "./dashboardSidebarFilters";

interface DashboardInventorySidebarProps {
  open: boolean;
  onClose: () => void;
  stockType: "out_of_stock" | "low_stock" | "incoming_stock" | null;
  scope?: "all" | "accessory";
  dateRange?: {
    from_date: string;
    to_date: string;
  } | null;
}

export const DashboardInventorySidebar: React.FC<DashboardInventorySidebarProps> = ({
  open,
  onClose,
  stockType,
  scope = "all",
  dateRange = null,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterValue, setFilterValue] = useState<DashboardColFilter[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const isOutOfStock = stockType === "out_of_stock";
  const isIncomingStock = stockType === "incoming_stock";
  const accentColor = isOutOfStock ? "#d2363a" : isIncomingStock ? "#0f9f6e" : "#e67e22";
  const bgColor = isOutOfStock ? "#fff1f1" : isIncomingStock ? "#ecfdf5" : "#fff7ed";
  const title = isOutOfStock
    ? "Out of Stock Items"
    : isIncomingStock
      ? "Incoming Stock Items"
      : "Low Stock Items";

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const fetchData = async () => {
    if (!stockType) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        stock_type: stockType,
        scope,
        page: String(page + 1),
        page_size: String(rowsPerPage),
      });
      if (search) params.set("search", search);
      if (dateRange?.from_date) params.set("from_date", dateRange.from_date);
      if (dateRange?.to_date) params.set("to_date", dateRange.to_date);
      if (dateRange?.from_date && dateRange?.to_date) params.set("period", "custom");
      if (sortField) {
        params.set(
          "ordering",
          sortDirection === "desc" ? `-${sortField}` : sortField,
        );
      }

      const filters = mergeDashboardFilters({}, filterValue);

      const response = await Axios.post(
        `/dashboard/sales/individual/stock-list?${params.toString()}`,
        { filters },
      );
      setData(response.data?.data || []);
      setTotalCount(response.data?.totalRecords || 0);
    } catch (error) {
      console.error("Failed to fetch inventory stock list:", error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    if (open && stockType) {
      fetchData();
    }
  }, [
    open,
    stockType,
    scope,
    dateRange,
    page,
    rowsPerPage,
    search,
    filterValue,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (open) {
      setPage(0);
      setSearchInput("");
      setSearch("");
      setSorting([]);
      setColumnFilters([]);
      setFilterValue([]);
    }
  }, [open, stockType, scope, dateRange]);

  useEffect(() => {
    if (open) setPage(0);
  }, [filterValue, sortField, sortDirection]);

  const columns = useMemo(() => {
    const stockExtra: Record<string, string | undefined> = {
      stock_type: stockType ?? undefined,
      scope,
      from_date: dateRange?.from_date,
      to_date: dateRange?.to_date,
      period:
        dateRange?.from_date && dateRange?.to_date ? "custom" : undefined,
    };

    return [
      {
        accessorKey: "sr_no",
        header: "#",
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info: any) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "'Poppins', sans-serif",
              color: "#6b7280",
            }}
          >
            {info.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "product_name",
        header: "Product Name",
        enableSorting: true,
        ...scopedDropdownProps("stock_list", "product_name", {}, stockExtra),
        cell: (info: any) => (
          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                color: "#111827",
              }}
            >
              {info.getValue()}
            </Typography>
            <Typography
              sx={{
                fontSize: "11px",
                color: "#6b7280",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {info.row.original.product_sku}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "make",
        header: isIncomingStock ? "Supplier / PO" : "Make / Model",
        enableSorting: true,
        ...scopedDropdownProps("stock_list", "make", {}, stockExtra),
        cell: (info: any) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "'Poppins', sans-serif",
              color: "#374151",
            }}
          >
            {info.getValue()} / {info.row.original.model}
          </Typography>
        ),
      },
      {
        accessorKey: "unit",
        header: isIncomingStock ? "ETA Date" : "Unit",
        enableSorting: true,
        enableColumnFilter: !isIncomingStock,
        ...(!isIncomingStock
          ? scopedDropdownProps("stock_list", "unit", {}, stockExtra)
          : {}),
        cell: (info: any) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "'Poppins', sans-serif",
              color: "#374151",
            }}
          >
            {info.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "category",
        header: "Type",
        enableSorting: !isIncomingStock,
        enableColumnFilter: !isIncomingStock,
        ...(!isIncomingStock
          ? scopedDropdownProps("stock_list", "category", {}, stockExtra)
          : {}),
        cell: (info: any) => (
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "'Poppins', sans-serif",
              color: "#374151",
            }}
          >
            {info.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "available_qty",
        header: isIncomingStock ? "Pending Qty" : "Qty",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (info: any) => {
          const qty = info.getValue() as number;
          return (
            <Chip
              label={qty}
              size="small"
              sx={{
                backgroundColor: qty <= 0 ? "#fff1f1" : isIncomingStock ? "#ecfdf5" : "#fff7ed",
                color: qty <= 0 ? "#d2363a" : isIncomingStock ? "#0f9f6e" : "#e67e22",
                fontWeight: 700,
                fontSize: "11px",
                borderRadius: "6px",
                height: 20,
                fontFamily: "'Poppins', sans-serif",
              }}
            />
          );
        },
      },
    ];
  }, [isIncomingStock, stockType, scope, dateRange]);

  const themeColor = MecordColors?.ThemeBGColor || accentColor;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          "@media (min-width: 768px)": {
            width: "calc(100vw - var(--tb-vertical-menu-width, 250px))",
          },
          p: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #f8f9fa 100%)",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          borderLeft: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "-16px 0 48px rgba(0, 0, 0, 0.16)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          pb: 1.5,
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            borderBottom: "1px solid #e5e7eb",
            mb: 2.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  backgroundColor: bgColor,
                  color: accentColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isOutOfStock ? <PackageX size={14} /> : <TriangleAlert size={14} />}
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  color: "#111827",
                  fontSize: "18px",
                }}
              >
                {title}
              </Typography>
              <Chip
                label="Inventory"
                size="small"
                sx={{
                  backgroundColor: bgColor,
                  color: accentColor,
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 20,
                  borderRadius: "6px",
                  fontFamily: "'Poppins', sans-serif",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "11px",
                color: "#6b7280",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              {isOutOfStock
                ? "Products with zero available inventory units"
                : isIncomingStock
                  ? "Items with pending Purchase Order deliveries"
                  : "Products with stock below their configured reorder level"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
            <CloseIcon size={20} />
          </IconButton>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by product name, SKU, make, model..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    sx={{ color: "#9ca3af" }}
                  >
                    <CloseIcon size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f9fafb",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: accentColor || themeColor },
              },
            }}
          />
        </Box>

        <Box
          className="sidebar-table-scroll-container"
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            backgroundColor: "#ffffff",
            position: "relative",
          }}
        >
          <MasterTableContainer
            columns={columns}
            data={data}
            totalRecords={totalCount}
            pageIndex={page}
            pageSize={rowsPerPage}
            onPageChange={setPage}
            onPageSizeChange={setRowsPerPage}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
          />
        </Box>
      </Box>
    </Drawer>
  );
};
